import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { EnhancedAnalyticsSummaryProps, CalculatedMetrics } from "../../../types/analytics"
import dayjs from "dayjs"
import { TruncatedText } from "../../ui/TruncatedText"
import { Box, Tooltip, Typography } from "@strapi/design-system"
import { InfoIcon } from "lucide-react"

export const EnhancedAnalyticsSummary: React.FC<EnhancedAnalyticsSummaryProps> = ({
  analyticsItems,
  productAnalytics,
  startOfCycle,
  showOverlay = false,
}) => {
  const { t } = useTranslation()
  const metrics = useMemo((): CalculatedMetrics => {
    const cycleStartDate = dayjs(startOfCycle)
    const filteredAnalytics = analyticsItems.filter(
      (item) => dayjs(item.date).isAfter(cycleStartDate) || dayjs(item.date).isSame(cycleStartDate)
    )

    const totalUniqueUsers = filteredAnalytics.reduce((sum, item) => sum + item.unique_users, 0)
    const totalVtoUsers = filteredAnalytics.reduce((sum, item) => sum + item.vto_users, 0)
    const totalVtoEvents = filteredAnalytics.reduce((sum, item) => sum + item.vto_events, 0)
    const totalAddToCartUsers = filteredAnalytics.reduce(
      (sum, item) => sum + item.add_to_cart_users,
      0
    )
    const totalCartActions = filteredAnalytics.reduce((sum, item) => sum + item.add_to_cart, 0)
    const rawEngagedUsers = filteredAnalytics.reduce((sum, item) => sum + item.engaged_users, 0)
    // Engaged users cannot exceed total unique users (engaged users are a subset of total users)
    const totalEngagedUsers = Math.min(rawEngagedUsers, totalUniqueUsers)
    const totalAddToCartEvents = filteredAnalytics.reduce((sum, item) => sum + item.add_to_cart, 0)

    const vtoConversionRate = totalUniqueUsers > 0 ? (totalVtoUsers / totalUniqueUsers) * 100 : 0
    const cartConversionRate =
      totalUniqueUsers > 0 ? (totalAddToCartUsers / totalUniqueUsers) * 100 : 0
    const vtoToCartRate = totalVtoUsers > 0 ? (totalAddToCartUsers / totalVtoUsers) * 100 : 0
    // Conversion rate: VTO to Cart (примерка в добавление в корзину)
    const addToCartConversionRate = vtoToCartRate

    const averageVtoSessions = totalVtoUsers > 0 ? totalVtoEvents / totalVtoUsers : 0
    const averageItemsPerCart = totalAddToCartUsers > 0 ? totalCartActions / totalAddToCartUsers : 0
    const avgVtoEventsPerUser = averageVtoSessions
    const avgSkusPerCart = averageItemsPerCart

    const productVtoTotals = productAnalytics.reduce((acc, item) => {
      // Skip items without product title
      if (!item.product_title) return acc

      if (!acc[item.product_title])
        acc[item.product_title] = {
          vtoEvents: 0,
          addToCartUsers: 0,
          uniqueUsers: 0,
          addToCartEvents: 0,
        }

      acc[item.product_title].vtoEvents += item.vto_events
      acc[item.product_title].addToCartUsers += item.add_to_cart_users
      acc[item.product_title].uniqueUsers += item.unique_users
      acc[item.product_title].addToCartEvents += item.add_to_cart
      return acc
    }, {} as Record<string, { vtoEvents: number; addToCartUsers: number; uniqueUsers: number; addToCartEvents: number }>)

    const topVtoProduct =
      Object.entries(productVtoTotals).sort(
        ([, a], [, b]) => (b as any).vtoEvents - (a as any).vtoEvents
      )[0]?.[0] || null

    const topAddToCartProduct =
      Object.entries(productVtoTotals).sort(
        ([, a], [, b]) => (b as any).addToCartEvents - (a as any).addToCartEvents
      )[0]?.[0] || null

    const bestConversionProduct =
      Object.entries(productVtoTotals)
        .map(([name, data]) => ({
          name,
          conversionRate:
            (data as any).uniqueUsers > 0
              ? ((data as any).addToCartUsers / (data as any).uniqueUsers) * 100
              : 0,
        }))
        .sort((a, b) => b.conversionRate - a.conversionRate)[0]?.name || null

    const totalProductsWithData = Object.keys(productVtoTotals).length

    return {
      vtoConversionRate,
      cartConversionRate,
      vtoToCartRate,
      averageVtoSessions,
      averageItemsPerCart,
      totalUniqueUsers,
      totalCartActions,
      totalVtoEvents,
      topVtoProduct,
      bestConversionProduct,
      totalProductsWithData,

      addToCartConversionRate,
      avgVtoEventsPerUser,
      totalEngagedUsers,
      totalAddToCartEvents,
      topAddToCartProduct,
      avgSkusPerCart,
      vtoConversionRateTrend: undefined,
      vtoConversionRateTrendValue: undefined,
      addToCartConversionRateTrend: undefined,
      addToCartConversionRateTrendValue: undefined,
      avgVtoEventsPerUserTrend: undefined,
      avgVtoEventsPerUserTrendValue: undefined,
      totalUniqueUsersTrend: undefined,
      totalUniqueUsersTrendValue: undefined,
      totalEngagedUsersTrend: undefined,
      totalEngagedUsersTrendValue: undefined,
      totalVtoEventsTrend: undefined,
      totalVtoEventsTrendValue: undefined,
      totalAddToCartEventsTrend: undefined,
      totalAddToCartEventsTrendValue: undefined,
      avgSkusPerCartTrend: undefined,
      avgSkusPerCartTrendValue: undefined,
    }
  }, [analyticsItems, productAnalytics, startOfCycle])

  const formatPercentage = (value: number | undefined) =>
    value !== undefined ? `${value.toFixed(1)}%` : "0.0%"
  const formatNumber = (value: number | undefined) =>
    value !== undefined ? value.toLocaleString() : "0"

  const MetricCard: React.FC<{
    title: string
    value: string | React.ReactNode
    subtitle?: string
    trend?: "up" | "down" | "stable"
    trendValue?: string
    variant?: "primary" | "secondary"
    tooltip?: string
  }> = ({ title, value, subtitle, trend, trendValue, variant = "primary", tooltip }) => {
    const { t } = useTranslation()
    const isPrimary = variant === "primary"

    return (
      <div
        className={`p-4 rounded-lg border ${
          isPrimary
            ? "bg-gradient-to-br from-gray-800/50 to-gray-700/30 border-gray-600/50"
            : "bg-gradient-to-br from-gray-700/50 to-gray-600/30 border-gray-500/50"
        }`}
      >
        <div className={`font-semibold ${isPrimary ? "text-gray-200" : "text-gray-300"} flex items-center gap-1`}>
          {title}
          {tooltip && (
            <Tooltip description={tooltip}>
              <Box className="flex items-center">
                <InfoIcon className="w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-300" />
              </Box>
            </Tooltip>
          )}
        </div>
        <div className="text-2xl font-light mt-2 text-white">{value}</div>
        {subtitle && <div className="text-sm text-gray-300 mt-1">{subtitle}</div>}
        {trend && trendValue && (
          <div className="flex items-center gap-1 mt-2">
            <div
              className={`text-sm font-medium ${
                trend === "up"
                  ? "text-green-400"
                  : trend === "down"
                  ? "text-red-400"
                  : "text-gray-300"
              }`}
            >
              {trend === "up" ? "↗" : trend === "down" ? "↘" : "→"} {trendValue}
            </div>
            <div className="text-sm text-gray-400">{t("analytics.summary.vsLastPeriod")}</div>
          </div>
        )}
      </div>
    )
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-dark-secondary rounded border border-gray-700">
        <h3 className="text-xl font-semibold mb-3 text-white">{t("analytics.summary.loading")}</h3>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="col-span-1">
              <div className="p-3 bg-gray-700 rounded h-[100px]" />
            </div>
          ))}
        </div>
      </div>
    )

  return (
    <div className="p-6 bg-dark-secondary rounded-lg border border-gray-700">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl analytics-section-header">{t("analytics.summary.title")}</h3>
            <p className="text-sm text-gray-300">{t("analytics.summary.subtitle")}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-300">
              {t("analytics.summary.dataFrom", { date: dayjs(startOfCycle).format("MMM D, YYYY") })}
            </div>
            <div className="text-sm text-gray-300">
              {t("analytics.summary.lastUpdated", { time: dayjs().format("MMM D, h:mm A") })}
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-lg font-semibold mb-3 text-white">
            {t("analytics.summary.performanceMetrics")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.vtoConversionRate")}
                value={formatPercentage(metrics.vtoConversionRate)}
                trend={metrics.vtoConversionRateTrend}
                trendValue={formatPercentage(metrics.vtoConversionRateTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.vtoConversionRateTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.addToCartConversion")}
                value={formatPercentage(metrics.addToCartConversionRate)}
                trend={metrics.addToCartConversionRateTrend}
                trendValue={formatPercentage(metrics.addToCartConversionRateTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.addToCartConversionTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.avgVtoEventsPerUser")}
                value={formatNumber(metrics.avgVtoEventsPerUser)}
                trend={metrics.avgVtoEventsPerUserTrend}
                trendValue={formatNumber(metrics.avgVtoEventsPerUserTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.avgVtoEventsPerUserTooltip")}
              />
            </div>
          </div>
        </div>

        {/* User Behavior */}
        <div>
          <h4 className="text-lg font-semibold mb-3 text-white">
            {t("analytics.summary.userBehavior")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalUsers")}
                value={formatNumber(metrics.totalUniqueUsers)}
                trend={metrics.totalUniqueUsersTrend}
                trendValue={formatNumber(metrics.totalUniqueUsersTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.totalUsersTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalVtoEvents")}
                value={formatNumber(metrics.totalVtoEvents)}
                trend={metrics.totalVtoEventsTrend}
                trendValue={formatNumber(metrics.totalVtoEventsTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.totalVtoEventsTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalAddToCartEvents")}
                value={formatNumber(metrics.totalAddToCartEvents)}
                trend={metrics.totalAddToCartEventsTrend}
                trendValue={formatNumber(metrics.totalAddToCartEventsTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.totalAddToCartEventsTooltip")}
              />
            </div>
          </div>
        </div>

        {/* Product Insights */}
        <div>
          <h4 className="text-lg font-semibold mb-3 text-white">
            {t("analytics.summary.productInsights")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.topVtoProduct")}
                value={
                  <TruncatedText
                    text={metrics.topVtoProduct || "N/A"}
                    maxLength={25}
                    className="text-white"
                  />
                }
                variant="primary"
                tooltip={t("analytics.summary.topVtoProductTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.topAddToCartProduct")}
                value={
                  <TruncatedText
                    text={metrics.topAddToCartProduct || "N/A"}
                    maxLength={25}
                    className="text-white"
                  />
                }
                variant="secondary"
                tooltip={t("analytics.summary.topAddToCartProductTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.avgSkusPerCart")}
                value={formatNumber(metrics.avgSkusPerCart)}
                trend={metrics.avgSkusPerCartTrend}
                trendValue={formatNumber(metrics.avgSkusPerCartTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.avgSkusPerCartTooltip")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
